import React, { CSSProperties, HTMLProps, useState } from "react";
import clsx from "clsx";
import { useTheme, withThemeProvider } from "@narvar/nth-hook-theme";
import { useStyles } from "./SelectItemStyles";

export type StyledSelectItemProps = {
  selected?: boolean;
  readOnly?: boolean;
  borderless?: boolean;
  transparent?: boolean;
  customStyle?: CSSProperties;
};

export type SelectItemProps = StyledSelectItemProps &
  Omit<HTMLProps<HTMLButtonElement>, "type" | "ref"> & {
    type?: "button" | "submit" | "reset";
    as?: "button" | "a" | "div" | "span";
  };

const SelectItem = ({
  onClick,
  selected,
  readOnly,
  disabled,
  borderless,
  transparent,
  customStyle,
  children,
}: SelectItemProps) => {
  const styles = useStyles();
  const {
    documentbodyNoneNone,
    bodytextDefaultNone,
    panelcontainerNoneNone,
    selecteditemNoneNone,
    buttonsSecondaryDisabled,
  } = useTheme();

  const [hover, setHover] = useState(false);

  const classes = clsx({
    [styles.selectItem]: true,
    [styles.hoverBorder]: !selected && !readOnly && !disabled && !borderless,
    [styles.noBorder]: borderless,
  });

  const defaultBackgroundColor =
    documentbodyNoneNone?.backgroundColor ??
    panelcontainerNoneNone?.backgroundColor;

  const borderStyle = {
    borderTopColor: selecteditemNoneNone?.borderTopColor ?? "#000",
    borderLeftColor: selecteditemNoneNone?.borderLeftColor ?? "#000",
    borderRightColor: selecteditemNoneNone?.borderRightColor ?? "#000",
    borderBottomColor: selecteditemNoneNone?.borderBottomColor ?? "#000",
  };

  return (
    <button
      className={classes}
      style={{
        ...bodytextDefaultNone,
        ...customStyle,
        ...(hover &&
          !selected &&
          !readOnly &&
          !disabled &&
          !borderless &&
          borderStyle),
        ...(hover &&
          !selected &&
          !readOnly &&
          !disabled &&
          !transparent && {
            backgroundColor: defaultBackgroundColor ?? "rgba(0, 0, 0, 0.05)",
            filter: defaultBackgroundColor ? "brightness(95%)" : undefined,
          }),
        ...(selected &&
          !borderless && {
            borderWidth: "2px",
            borderStyle: "solid",
            ...borderStyle,
          }),
        ...(selected && {
          backgroundColor: !transparent
            ? (selecteditemNoneNone?.backgroundColor ?? defaultBackgroundColor)
            : undefined,
        }),
        ...(readOnly && { cursor: "inherit" }),
        ...(disabled && {
          cursor: "not-allowed",
          color: buttonsSecondaryDisabled?.color ?? "#999",
        }),
        ...(transparent && { background: "inherit" }),
        ...(borderless && { border: "none" }),
      }}
      onClick={readOnly || disabled ? undefined : onClick}
      onMouseEnter={() => setHover(!hover)}
      onMouseLeave={() => setHover(!hover)}>
      {children}
    </button>
  );
};

export default withThemeProvider(SelectItem, {
  themeTokens: [
    "documentbodyNoneNone",
    "bodytextDefaultNone",
    "panelcontainerNoneNone",
    "selecteditemNoneNone",
    "buttonsSecondaryDisabled",
  ],
});
